import type {
  RdAnswerItem,
  RdMapGeometryCreatePayload,
  RdMapGeometryItem,
  RdRequestBowCharacteristics,
  RdRequestBowPatchPayload,
  RdRequestCardAuditEntry,
  RdRequestCardResponse,
  RdRequestCardUpdatePayload,
  RdRequestCreatePayload,
  RdRequestDetail,
  RdRequestListResponse,
  RdRequestMeta,
  RdRequestPatchPayload,
  RdRequestFileItem,
} from "@/types/rd-request";
import { apiRequest, postFormData } from "@/lib/api-client-v2";
import type { FilestoreNodeBrief, Geometry, GeometryAnalyzeResponse } from "@/types/api-v2";

class RdRequestsService {
  private readonly baseUrl = "/rd-requests";

  async list(params?: {
    skip?: number;
    limit?: number;
    kls_id_type?: number;
    request_source?: string;
  }): Promise<RdRequestListResponse> {
    return apiRequest<RdRequestListResponse>(this.baseUrl, {
      params: {
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 50,
        ...(params?.kls_id_type != null ? { kls_id_type: params.kls_id_type } : {}),
        ...(params?.request_source ? { request_source: params.request_source } : {}),
      },
    });
  }

  async getById(requestId: number): Promise<RdRequestDetail> {
    return apiRequest<RdRequestDetail>(`${this.baseUrl}/${requestId}`);
  }

  async getMeta(): Promise<RdRequestMeta> {
    return apiRequest<RdRequestMeta>(`${this.baseUrl}/meta`);
  }

  async getCard(requestId: number): Promise<RdRequestCardResponse> {
    return apiRequest<RdRequestCardResponse>(`${this.baseUrl}/${requestId}/card`);
  }

  async updateCard(requestId: number, payload: RdRequestCardUpdatePayload): Promise<RdRequestCardResponse> {
    return apiRequest<RdRequestCardResponse>(`${this.baseUrl}/${requestId}/card`, {
      method: "PUT",
      data: payload,
    });
  }

  async listCardAudit(requestId: number, params?: { limit?: number }): Promise<RdRequestCardAuditEntry[]> {
    return apiRequest<RdRequestCardAuditEntry[]>(`${this.baseUrl}/${requestId}/card/audit`, {
      params:
        params?.limit != null
          ? { limit: params.limit }
          : undefined,
    });
  }

  async create(payload: RdRequestCreatePayload): Promise<RdRequestDetail> {
    return apiRequest<RdRequestDetail>(this.baseUrl, {
      method: "POST",
      data: {
        kls_id_type: payload.kls_id_type ?? 1,
        request_source: payload.request_source,
        request_doc_num: payload.request_doc_num,
        request_doc_date: payload.request_doc_date,
        request_maker: payload.request_maker,
        request_maker_tel: payload.request_maker_tel,
        request_content: payload.request_content,
      },
    });
  }

  async patch(requestId: number, payload: RdRequestPatchPayload): Promise<RdRequestDetail> {
    return apiRequest<RdRequestDetail>(`${this.baseUrl}/${requestId}`, {
      method: "PATCH",
      data: payload,
    });
  }

  async deleteRequest(requestId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${requestId}`, {
      method: "DELETE",
    });
  }

  async getCharacteristics(requestId: number): Promise<RdRequestBowCharacteristics> {
    return apiRequest<RdRequestBowCharacteristics>(`${this.baseUrl}/${requestId}/characteristics`);
  }

  async patchCharacteristics(
    requestId: number,
    payload: RdRequestBowPatchPayload,
  ): Promise<RdRequestBowCharacteristics> {
    return apiRequest<RdRequestBowCharacteristics>(`${this.baseUrl}/${requestId}/characteristics`, {
      method: "PATCH",
      data: payload,
    });
  }

  async listFiles(requestId: number): Promise<RdRequestFileItem[]> {
    return apiRequest<RdRequestFileItem[]>(`${this.baseUrl}/${requestId}/files`);
  }

  async uploadFile(
    requestId: number,
    file: File,
    options?: { description?: string; parentNodeId?: number | null },
  ): Promise<RdRequestFileItem> {
    const formData = new FormData();
    formData.append("file", file);
    if (options?.description && options.description.trim()) {
      formData.append("description", options.description.trim());
    }
    if (options?.parentNodeId != null) {
      formData.append("parent_node_id", String(options.parentNodeId));
    }
    return postFormData<RdRequestFileItem>(`${this.baseUrl}/${requestId}/files`, formData);
  }

  async deleteFile(requestId: number, fileId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${requestId}/files/${fileId}`, {
      method: "DELETE",
    });
  }

  async getFilestoreNodes(requestId: number, parentId: number | null): Promise<FilestoreNodeBrief[]> {
    return apiRequest<FilestoreNodeBrief[]>(
      `${this.baseUrl}/${requestId}/filestore/nodes`,
      parentId != null ? { params: { parent_id: parentId } } : undefined,
    );
  }

  async createFilestoreFolder(
    requestId: number,
    payload: { name: string; parent_id?: number | null; description?: string },
  ): Promise<{ id: number; name: string; parent_id: number | null; kind: string }> {
    return apiRequest<{ id: number; name: string; parent_id: number | null; kind: string }>(
      `${this.baseUrl}/${requestId}/filestore/folders`,
      {
        method: "POST",
        data: {
          name: payload.name,
          parent_id: payload.parent_id ?? null,
          description: payload.description,
        },
      },
    );
  }

  async listMapGeometries(requestId: number): Promise<RdMapGeometryItem[]> {
    return apiRequest<RdMapGeometryItem[]>(`${this.baseUrl}/${requestId}/map-geometries`);
  }

  async createMapGeometry(requestId: number, payload: RdMapGeometryCreatePayload): Promise<RdMapGeometryItem> {
    return apiRequest<RdMapGeometryItem>(`${this.baseUrl}/${requestId}/map-geometries`, {
      method: "POST",
      data: payload,
    });
  }

  async deleteMapGeometry(requestId: number, requestMapGeometryId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${requestId}/map-geometries/${requestMapGeometryId}`, {
      method: "DELETE",
    });
  }

  async analyzeMapGeometry(
    requestId: number,
    requestMapGeometryId: number,
    payload?: { tolerance_meters?: number; limit?: number; ratio_threshold?: number },
  ): Promise<GeometryAnalyzeResponse> {
    return apiRequest<GeometryAnalyzeResponse>(
      `${this.baseUrl}/${requestId}/map-geometries/${requestMapGeometryId}/analyze`,
      {
        method: "POST",
        data: {
          tolerance_meters: payload?.tolerance_meters ?? 3500,
          limit: payload?.limit ?? 50,
          ratio_threshold: payload?.ratio_threshold ?? 0.9,
        },
      },
    );
  }

  async getGeometryById(geometryId: number): Promise<Geometry> {
    return apiRequest<Geometry>(`/geometries/${geometryId}`);
  }

  async listAnswers(requestId: number): Promise<RdAnswerItem[]> {
    return apiRequest<RdAnswerItem[]>(`${this.baseUrl}/${requestId}/answers`);
  }
}

export const rdRequestsApi = new RdRequestsService();

